import { BinaryTree, BTTYPE, TEMPLATE_NODE } from './binary-tree';
import { BTENPAR } from './binary-tree-edit-node';
import { ScriptNode } from './script-node';
import { XItem } from './x-item'

export type BTNR = 'accepted' | 'rejected' | 'postponed';

export type BTNMETA = {
    title?: string;
    role?: BTNR;
    description?: string;
    script?: string;
    exclusiveChild?: boolean;
};

export type BTNPAR = {
    id?: string;
    meta?: BTNMETA;
    childLs?: BTNPAR[];
    parent?: BinaryTreeNode;
};

export type BTNSTATPAR = {
    btn: BinaryTreeNode;
    count: number;
    totalCount: number;
    childLs?: BTNSTATPAR[];
    level?: number;
};

export type ACTIVENODE = {
    nd: BinaryTreeNode;
    activeChildLs: ACTIVENODE[];
};

export class BinaryTreeNode {
    id: string;

    parent: BinaryTreeNode | undefined;
    childLs: BinaryTreeNode[] = [];
    meta: BTNMETA;

    private _compiledOptionFun?: Function;

    constructor(
        public binaryTree: BinaryTree,
        params: BTNPAR = {},
    ) {
        this.id = params.id || this.binaryTree.coords.uniqueId();
        this.parent = params.parent;
        this.meta = Object.assign({}, params.meta);

        params.childLs &&
            params.childLs.forEach((x) => {
                x.parent = this;
                this.childLs.push(new BinaryTreeNode(this.binaryTree, x));
            });
    }

    get coords() {
        return this.binaryTree.coords;
    }

    get btenPar(): BTENPAR {
        return {
            id: this.id,
            meta: Object.assign({}, this.meta),
            childLs: this.childLs.map((x) => x.btenPar),
        };
    }

    get script() {
        return this.meta.script || '';
    }

    set script(val: string) {
        this.meta.script = val;
    }

    get isDisabled() {
        return this.testItem(this.coords.resultList$.activeItem!) === 'disable';
    }

    get isHidden() {
        return this.testItem(this.coords.resultList$.activeItem!) === 'hide';
    }

    private _compileOptionFun() {
        if (!this.script?.trim()) return;
        try {
            this._compiledOptionFun = this.coords.utils$.compileScriptFunction(this.script);
        } catch (error) {
            console.error('CONDITION FIELD SCRIPT ERROR', this.script, error);
        }
    }

    testItem(xitm: XItem) {
        if (!this.script) return '';
        if (!this._compiledOptionFun) this._compileOptionFun();
        if (!this._compiledOptionFun) return '';
        try {
            return this._compiledOptionFun(xitm.scriptClass);
        } catch (error: any) {
            console.log('FORMULA ERROR', this.script, error);
            return {
                isError: true,
                message: error.message,
            };
        }
    }

    activeIdSet(isAll = false) {
        const s = new Set(this._isOnSet);
        if (isAll) {
            this.coords.project$.current!.team?.activeIdSet(this).forEach((x) => s.add(x));
        }
        return s;
    }

    selectedIdSet(userIdLs?: string[]): Set<string> {
        if (!userIdLs) return new Set(this._isOnSet);
        const cuId = this.coords.user$.currentId;
        const s = new Set<string>(userIdLs.includes(cuId) ? this._isOnSet : []);
        const t = this.coords.project$.current!.team;
        if (t) {
            userIdLs.forEach((userId) => {
                if (userId === cuId) return;
                const pm = t.getMember(userId);
                pm && pm.activeIdSet(this, s);
            });
        }

        userIdLs.forEach((userId) => {
            const au = this.coords.autouser$.get(userId);
            au && au.activeIdSet(this, s);
        });

        return s;
    }

    private get _isOnSet() {
        return this.binaryTree.getNodeOnSet(this);
    }

    private set _isOnSet(s: Set<string>) {
        this.binaryTree.setNodeOnSet(this, s);
    }

    updateFromMap(mp: Map<string, Set<string>>): void {
        const s = mp.get(this.id);
        if (s) this._isOnSet = s;
        else this._isOnSet.clear();
        this.childLs.forEach((x) => x.updateFromMap(mp));
    }

    isOn(id: string) {
        return this._isOnSet.has(id);
    }

    setIsOn(id: string, value: boolean, isStart = true) {
        let ls: BinaryTreeNode[];
        if (value) {
            this._isOnSet.add(id);
            ls = this.ancestors;
        } else {
            // if (this.parent?.exclusiveChild)
            //     this._unsetChildLs(id)
            this._isOnSet.delete(id);
            ls = this.descendants;
        }
        if (isStart) {
            ls?.forEach((x) => x.setIsOn(id, value, false));
            if (value && this.binaryTree.type === 'ratings' && this.parent === this.binaryTree.root)
                this.parent.childLs.forEach((x) => x !== this && x.setIsOn(id, false, false));
            this.binaryTree.manifestValuesStore();
        }
        this.coords.display$.markForCheck();
    }

    get exclusiveChild() {
        return this.meta.exclusiveChild || false;
    }

    get role() {
        return this.meta.role;
    }

    get ancestors() {
        const ls: BinaryTreeNode[] = [];
        this.parent?._ancestors(ls);
        return ls;
    }

    private _createNodeFromPath(pathLs: string[]) {
        const nd = new BinaryTreeNode(this.binaryTree, {
            parent: this,
            meta: { title: pathLs.shift() },
        });
        this.childLs.push(nd);
        if (pathLs.length) nd._createNodeFromPath(pathLs);
        return nd;
    }

    getNodeFromId(nodeId: string): BinaryTreeNode | null {
        if (this.id === nodeId) return this;
        for (const ch of this.childLs) {
            if (ch.id === nodeId) return ch;
            const nd = ch.getNodeFromId(nodeId);
            if (nd) return nd;
        }
        return null;
    }

    getNodeFromPath(pathLs: string | string[], isCreate = false): BinaryTreeNode | null {
        if (typeof pathLs === 'string') pathLs = pathLs.split('/').filter((x) => x);
        const topNode = pathLs.shift();
        const title = topNode?.toLowerCase();
        if (!title) return null;
        for (const ch of this.childLs) {
            if (ch.title.toLowerCase() !== title) continue;
            if (pathLs.length) {
                const nd = ch.getNodeFromPath(pathLs, isCreate);
                if (nd) return nd;
            } else {
                return ch;
            }
        }
        if (isCreate) {
            const nd = this._createNodeFromPath([topNode!, ...pathLs]);
            this.binaryTree.markForManifestStore();
            return nd;
        } else return null;
    }

    private _ancestors(ls: BinaryTreeNode[]) {
        if (!this.parent) {
            return;
        }
        ls.push(this);
        this.parent._ancestors(ls);
    }

    get descendants() {
        const ls: BinaryTreeNode[] = [];
        this._descendants(ls);
        return ls;
    }

    private _descendants(ls: BinaryTreeNode[]) {
        this.childLs.forEach((x) => {
            ls.push(x);
            x._descendants(ls);
        });
    }

    get path(): string {
        if (this.parent) return `${this.parent.path}/${this.title?.toLowerCase()}`;
        return '';
    }

    get displayPath(): string {
        if (this.parent) return `${this.parent.path}/${this.title}`;
        return '';
    }

    get title() {
        return this.meta.title || '';
    }

    set title(val: string) {
        this.meta.title = val || this.meta.title || '';
        this.binaryTree.markForManifestStore();
    }

    get description() {
        return this.meta.description || '';
    }

    set description(val: string) {
        this.meta.description = val || '';
        this.binaryTree.markForManifestStore();
    }

    forSaveScript() {
        let txt = this.path ? `\n rt.node("${this.path}", true) ` : '';
        this.childLs.forEach((n) => {
            txt += n.forSaveScript();
        });
        return txt;
    }

    exportJsonAll(): any {
        return {
            id: this.id,
            title: this.title,
            childList: this.childLs.map((x) => x.exportJsonAll()),
        };
    }

    save() {
        const dct: BTNPAR = {
            id: this.id,
            meta: Object.assign({}, this.meta),
        };

        if (this.childLs.length) dct.childLs = this.childLs.map((ch) => ch.save());
        return dct;
    }

    submitBulk(xitmIdLs: string[], selectedNodesSet: Set<string>) {
        if (selectedNodesSet.has(this.id)) {
            xitmIdLs.forEach((xitmId) => this._isOnSet.add(xitmId));
        } else {
            xitmIdLs.forEach((xitmId) => this._isOnSet.delete(xitmId));
        }

        this.childLs.forEach((ch) => ch.submitBulk(xitmIdLs, selectedNodesSet));
    }

    addIds(s: Set<string>) {
        s.forEach((x) => this.setIsOn(x, true));
    }

    deleteIds(s: Set<string>) {
        s.forEach((x) => this.setIsOn(x, false));
    }

    delete() {
        this.parent?.childLs.splice(this.parent?.childLs.indexOf(this), 1);
        this.binaryTree.markForManifestStore();
    }

    stats(): BTNSTATPAR {
        return {
            btn: this,
            count: this._isOnSet.size,
            totalCount: this.activeIdSet(true).size,
            childLs: this.childLs.map((x) => x.stats()),
        };
    }

    preview(isAll = false) {
        const t: BTTYPE = this.binaryTree.type!;
        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: new Set(isAll ? this.activeIdSet(true) : this._isOnSet),
            title: this.displayPath,
            prefix: this.binaryTree.title,
            icon: t,
            class: 'selection-in-general',
            back: () => this.coords.stage$.set(stg),
            stage: 'collection',
        });
    }

    getActiveSubtree(nodeIdLs: string[]): ACTIVENODE | null {
        if (nodeIdLs.includes(this.id))
            return {
                nd: this,
                activeChildLs: this.childLs
                    .map((x) => x.getActiveSubtree(nodeIdLs))
                    .filter((x) => x) as ACTIVENODE[],
            };
        else return null;
    }

    get scriptClass() {
        return new ScriptNode(this.coords, this);
    }

    createNodeFromTemplate(params: TEMPLATE_NODE) {
        if (params.id) {
            for (const ch of this.childLs) {
                if (ch.id === params.id) return;
            }
        }
        const chNd = new BinaryTreeNode(this.binaryTree, {
            id: params.id,
            meta: { title: params.title, description: params.description },
        });
        if (params.children) {
            params.children.forEach((x) => chNd.createNodeFromTemplate(x));
        }
        this.childLs.push(chNd);
        chNd.parent = this;
    }
}
